<template>
  <div class="configDriveBackupVue">
    <div class="col-md-10 col-sm-9 u-pad-all-25 c-common-box-shadow u-bg-color-fff">
      <h3 class="u-cmn-botmargin">Drive Backup</h3>
      <p class="o-blue-regular-text">
        Back up your tenant's CS Drive files to storage <strong>you</strong> control.
        Credentials are stored encrypted in a separate vault and are never shown again.
      </p>

      <p class="o-src-line">
        <i class="fa fa-folder-open" aria-hidden="true"></i>
        <strong>Source:</strong> your Workspace Drive (this tenant's folder)
      </p>

      <div class="row">
        <!-- ── Target config ─────────────────────────────────────────── -->
        <div class="col-md-6">
          <h4>Backup Target</h4>

          <label>Target type</label>
          <el-select v-model="form.type" placeholder="Select a target" style="width:100%;" popper-class="o-target-popper" @change="onTypeChange">
            <el-option-group v-for="group in targetGroups" :key="group.label" :label="group.label">
              <el-option v-for="t in group.options" :key="t.value" :label="t.label" :value="t.value" :disabled="!!t.disabled"></el-option>
            </el-option-group>
          </el-select>

          <div v-if="form.type" class="c-margin-top-10">
            <!-- On-prem is Tailscale-first: choose Tailscale, then the protocol,
                 then the endpoint + path + credentials. -->
            <template v-if="form.type === 'tailscale'">
              <label>Connection protocol</label>
              <el-select v-model="form.protocol" placeholder="Select a protocol" style="width:100%;" @change="onProtocolChange">
                <el-option v-for="p in protocolOptions" :key="p.value" :label="p.label" :value="p.value"></el-option>
              </el-select>
              <p class="o-protocol-hint">
                Not sure which? Choose <strong>SMB</strong> for a Windows/office file share or most
                NAS shared folders; choose <strong>SFTP</strong> for a Linux server or a NAS with SSH
                enabled. Both run securely inside your Tailscale connection.
              </p>

              <div class="o-ts-callout c-margin-top-10">
                <i class="fa fa-shield" aria-hidden="true"></i>
                After you paste a Tailscale auth key, CallSteward joins YOUR tailnet as node
                <strong>callsteward-backup</strong> only during a backup job then disconnects.
                Restrict it via your Tailscale ACLs to only your storage host, port 22 (SFTP)
                or 445 (SMB). Revoke the key any time to cut access.
              </div>

              <div class="c-margin-top-10 c-margin-bottom-10">
                <label>Tailscale auth key</label>
                <input
                  type="password"
                  class="form-control"
                  v-model="form.tailscale_auth_key"
                  placeholder="tskey-auth-…"
                  autocomplete="new-password" />
              </div>

              <div v-if="form.protocol">
                <div v-for="field in fieldsFor(form.protocol)" :key="field.key" class="c-margin-bottom-10">
                  <label>{{ field.label }}</label>
                  <input
                    :type="field.secret ? 'password' : 'text'"
                    class="form-control"
                    v-model="form[field.key]"
                    :placeholder="field.placeholder || ''"
                    autocomplete="new-password" />
                </div>
              </div>
            </template>

            <!-- Cloud / other target types keep the flat field list. -->
            <template v-else>
              <!-- Google Workspace Drive: service-account-key setup guidance. -->
              <div v-if="form.type === 'gdrive'" class="o-target-instructions c-margin-bottom-10">
                <strong>Back up to a folder in your own Google Drive.</strong>
                You'll create a service account (a robot user), then share one folder with it.
                <ol>
                  <li>In <strong>Google Cloud Console → IAM &amp; Admin → Service Accounts</strong>, click
                    <strong>Create service account</strong>, then under <strong>Keys → Add key → JSON</strong>
                    download the key file.</li>
                  <li>In <strong>Google Drive</strong>, create or pick the destination folder and
                    <strong>Share</strong> it with the service account's email
                    (<code>…@…iam.gserviceaccount.com</code>) as <strong>Editor</strong>.
                    <em>For a Shared Drive, add the service account as a member instead.</em></li>
                  <li>Paste the <strong>JSON key</strong> below and enter the <strong>Folder ID</strong>
                    (the last part of the folder's URL). If it's a Shared Drive, also enter the
                    <strong>Shared Drive ID</strong>.</li>
                </ol>
                CallSteward can then write <strong>only to the folder you shared</strong> — nothing else in
                your Drive. Revoke access any time by un-sharing the folder or deleting the key.
              </div>

              <!-- Google Cloud Storage: service-account-key setup guidance. -->
              <div v-if="form.type === 'gcs'" class="o-target-instructions c-margin-bottom-10">
                <strong>Back up to your own Google Cloud Storage bucket.</strong>
                <ol>
                  <li>In <strong>Google Cloud Console → IAM &amp; Admin → Service Accounts</strong>,
                    <strong>Create service account</strong>, then <strong>Keys → Add key → JSON</strong>
                    and download it.</li>
                  <li>In <strong>Cloud Storage</strong>, create or pick the destination <strong>bucket</strong>
                    and grant the service account <strong>Storage Object Admin</strong> on it
                    (Bucket → Permissions → Grant access → paste the SA email → role
                    <strong>Storage Object Admin</strong>).</li>
                  <li>Paste the <strong>JSON key</strong> below and enter the <strong>bucket name</strong>
                    (and optional path prefix).</li>
                </ol>
                CallSteward can then write <strong>only to that bucket</strong> — nothing else in your
                project. Revoke access any time by removing the SA's role or deleting the key.
              </div>

              <!-- Amazon S3 / S3-compatible: keyed access-key setup guidance. -->
              <div v-if="form.type === 's3'" class="o-target-instructions c-margin-bottom-10">
                <strong>Back up to your own S3 bucket</strong> (or any S3-compatible store — Wasabi,
                Cloudflare R2, Backblaze B2, DigitalOcean Spaces, MinIO).
                <ol>
                  <li>Create or pick the destination <strong>bucket</strong>.</li>
                  <li>Create an access key scoped to just that bucket: in <strong>AWS IAM → Users →
                    Create user</strong>, attach an inline policy allowing
                    <code>s3:PutObject</code>/<code>s3:GetObject</code>/<code>s3:ListBucket</code> on
                    <strong>only that bucket's ARN</strong>, then <strong>Security credentials →
                    Create access key</strong>.</li>
                  <li>Paste the <strong>Access key ID</strong> + <strong>Secret access key</strong> and
                    enter the <strong>bucket name</strong> (and optional path prefix). For a non-AWS
                    S3 store, also enter its <strong>endpoint</strong> and <strong>region</strong>.</li>
                </ol>
                CallSteward writes <strong>only to that bucket</strong>. Rotate or delete the access
                key any time.
              </div>

              <!-- OneDrive / SharePoint: OAuth (Connect) setup guidance. -->
              <div v-if="form.type === 'onedrive'" class="o-target-instructions c-margin-bottom-10">
                <p class="o-coming-soon">
                  Coming soon — OneDrive/SharePoint support is being finalized. Use another target for now.
                </p>
                <strong>Back up to your OneDrive or SharePoint.</strong>
                <ol>
                  <li>Click <strong>Connect OneDrive</strong> below.</li>
                  <li>Sign in with your Microsoft account and <strong>approve</strong> CallSteward to
                    save files to your OneDrive/SharePoint.</li>
                  <li>Choose a destination folder (optional path).</li>
                </ol>
                You can revoke CallSteward's access any time from your Microsoft account's
                <strong>App permissions</strong>.
                <p class="o-dep-note">
                  ⚠️ Requires the CallSteward Microsoft (Azure AD) app to be registered +
                  admin-consented; until then the Connect button won't complete.
                </p>
              </div>

              <!-- Dropbox: OAuth (Connect) setup guidance. -->
              <div v-if="form.type === 'dropbox'" class="o-target-instructions c-margin-bottom-10">
                <strong>Back up to your Dropbox.</strong>
                <ol>
                  <li>Click <strong>Connect Dropbox</strong> below.</li>
                  <li>Sign in and <strong>allow</strong> CallSteward to add files to your Dropbox (it
                    gets its own app folder — it can't see the rest of your Dropbox).</li>
                  <li>Choose a destination subfolder (optional path).</li>
                </ol>
                Revoke any time from Dropbox <strong>Settings → Connected apps</strong>.
                <p class="o-dep-note">
                  ⚠️ Requires the CallSteward Dropbox app to be registered; until then the Connect
                  button won't complete.
                </p>
              </div>

              <div v-for="field in fieldsFor(form.type)" :key="field.key" class="c-margin-bottom-10">
                <label>{{ field.label }}</label>
                <textarea
                  v-if="field.textarea"
                  class="form-control o-sa-key"
                  v-model="form[field.key]"
                  :placeholder="field.placeholder || ''"
                  rows="6"
                  autocomplete="new-password"></textarea>
                <input
                  v-else
                  :type="field.secret ? 'password' : 'text'"
                  class="form-control"
                  v-model="form[field.key]"
                  :placeholder="field.placeholder || ''"
                  autocomplete="new-password" />
              </div>
            </template>

            <div v-if="isOauthType(form.type)" class="c-margin-bottom-10">
              <a href="javascript: void(0)" @click="connectOauth()" class="c-common-btn c-common-btn--small">
                <i class="fa fa-plug" aria-hidden="true"></i> Connect {{ labelFor(form.type) }}
              </a>
              <span v-if="form.oauth_code" style="color:#3c763d;margin-left:8px;">Connected ✓</span>
            </div>

            <div v-if="showControls">
              <div class="checkbox o-custom-radio c-margin-bottom-10">
                <label>
                  <input type="checkbox" v-model="form.schedule_nightly">
                  <span class="cr"><i class="cr-icon fa fa-check"></i></span>
                </label>
                <label class="o-blue-regular-text">Also back up nightly</label>
              </div>

              <a href="javascript: void(0)" @click="testConnection()" :class="{'is-busy': testing}" class="c-common-btn c-common-btn--small c-common-btn--inverse-icon">
                <i class="fa fa-bolt" aria-hidden="true"></i> Test connection
              </a>
              <a href="javascript: void(0)" @click="saveTarget()" class="c-solid-btn c-solid-btn--green">Save target</a>
            </div>
          </div>
        </div>

        <!-- ── Existing targets + Sync now ───────────────────────────── -->
        <div class="col-md-6">
          <h4>Configured Targets</h4>
          <div v-if="targets.length === 0" style="padding:10px;color:#999;">No targets configured yet.</div>
          <ul class="o-plain-list">
            <li v-for="t in targets" :key="t.id" class="c-margin-bottom-10">
              <strong>{{ t.name || labelFor(t.type) }}</strong>
              <span class="o-blue-regular-text"> — {{ labelFor(t.type) }}</span>
              <a href="javascript: void(0)" @click="syncNow(t.id)" class="c-common-btn c-common-btn--small pull-right">
                <i class="fa fa-refresh" aria-hidden="true"></i> Sync now
              </a>
            </li>
          </ul>
          <a v-if="targets.length" href="javascript: void(0)" @click="syncNow(null)" class="c-solid-btn c-solid-btn--green">
            <i class="fa fa-refresh" aria-hidden="true"></i> Sync all now
          </a>
        </div>
      </div>

      <!-- ── Sync history (from the ledger) ──────────────────────────── -->
      <div class="row c-margin-top-25">
        <div class="col-md-12">
          <h4>Sync History</h4>
          <table class="table table-striped">
            <thead>
              <tr><th>File</th><th>Status</th><th>Attempts</th><th>Last attempt</th><th>Error</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in history" :key="row.id">
                <td>{{ row.file_id }}</td>
                <td><span :class="statusClass(row.status)">{{ row.status }}</span></td>
                <td>{{ row.attempts }}</td>
                <td>{{ row.last_attempt_at || '—' }}</td>
                <td class="o-truncate" :title="row.last_error">{{ row.last_error || '' }}</td>
              </tr>
              <tr v-if="history.length === 0"><td colspan="5" style="color:#999;">No sync activity yet.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import service from '../../services.js';

export default {
  data() {
    return {
      testing: false,
      targets: [],
      history: [],
      form: this.blankForm(),
      protocolOptions: [
        { value: 'sftp', label: 'SFTP / SSH' },
        { value: 'smb', label: 'SMB / CIFS (office share)' },
      ],
      targetGroups: [
        {
          label: 'On-prem (via Tailscale)',
          options: [
            { value: 'tailscale', label: 'On-prem storage (Tailscale)' },
          ],
        },
        {
          label: 'Cloud',
          options: [
            { value: 's3', label: 'Amazon S3 / S3-compatible' },
            { value: 'onedrive', label: 'OneDrive / SharePoint (Coming Soon)', disabled: true },
            { value: 'dropbox', label: 'Dropbox' },
          ],
        },
        {
          label: 'Google',
          options: [
            { value: 'gcs', label: 'Google Cloud Storage' },
            { value: 'gdrive', label: 'Workspace Drive' },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      userToken: 'userToken',
      userRole: 'userRole',
      userClientID: 'userClientID',
    }),
    // On-prem (Tailscale) reveals the save/test controls only once a protocol is
    // chosen; every other target type shows them as soon as the type is picked.
    showControls() {
      return !!this.form.type && (this.form.type !== 'tailscale' || !!this.form.protocol);
    },
  },
  methods: {
    blankForm() {
      return {
        type: '', protocol: '', name: '', host: '', port: '', share: '', path: '',
        username: '', password: '', key: '', access_key: '', secret_key: '',
        endpoint: '', region: '', tailscale_auth_key: '', bucket: '', folder_id: '',
        team_drive: '', service_account_json: '', oauth_code: '', schedule_nightly: false,
      };
    },
    headers() {
      return {
        Authorization: this.userToken,
        'cs3-context-role': this.userRole,
        'cs3-context-clientId': this.userClientID,
      };
    },
    fieldsFor(type) {
      const map = {
        sftp: [
          { key: 'name', label: 'Name (optional)' },
          { key: 'host', label: 'Host', placeholder: 'nas.example.com' },
          { key: 'port', label: 'Port', placeholder: '22' },
          { key: 'path', label: 'Path', placeholder: '/backups/callsteward' },
          { key: 'username', label: 'Username' },
          { key: 'password', label: 'Password', secret: true },
          { key: 'key', label: 'Private key (optional)', secret: true },
        ],
        smb: [
          { key: 'name', label: 'Name (optional)' },
          { key: 'host', label: 'Host', placeholder: '\\\\fileserver' },
          { key: 'share', label: 'Share', placeholder: 'backups' },
          { key: 'path', label: 'Path (optional)', placeholder: 'callsteward' },
          { key: 'username', label: 'Username' },
          { key: 'password', label: 'Password', secret: true },
        ],
        s3: [
          { key: 'name', label: 'Name (optional)' },
          { key: 'bucket', label: 'Bucket' },
          { key: 'path', label: 'Prefix (optional)', placeholder: 'callsteward/' },
          { key: 'access_key', label: 'Access key ID', secret: true },
          { key: 'secret_key', label: 'Secret access key', secret: true },
          { key: 'endpoint', label: 'Endpoint (S3-compatible only)', placeholder: 'https://…' },
          { key: 'region', label: 'Region (S3-compatible only)', placeholder: 'us-east-1' },
        ],
        gcs: [
          { key: 'name', label: 'Name (optional)' },
          { key: 'bucket', label: 'Bucket', placeholder: 'my-backup-bucket' },
          { key: 'path', label: 'Path (optional)', placeholder: 'callsteward/' },
          { key: 'service_account_json', label: 'Service account JSON key', secret: true, textarea: true, placeholder: '{ "type": "service_account", "project_id": "…", … }' },
        ],
        gdrive: [
          { key: 'name', label: 'Name (optional)' },
          { key: 'folder_id', label: 'Folder ID', placeholder: 'last part of the folder URL' },
          { key: 'team_drive', label: 'Shared Drive ID (optional)' },
          { key: 'service_account_json', label: 'Service account JSON key', secret: true, textarea: true, placeholder: '{ "type": "service_account", "project_id": "…", … }' },
        ],
      };
      return map[type] || [{ key: 'name', label: 'Name (optional)' }, { key: 'path', label: 'Folder (optional)' }];
    },
    isOauthType(type) {
      return ['onedrive', 'dropbox'].includes(type);
    },
    labelFor(type) {
      for (const g of this.targetGroups) {
        const found = g.options.find((o) => o.value === type);
        if (found) return found.label;
      }
      // Saved on-prem targets carry their protocol (sftp/smb) as the type.
      const proto = this.protocolOptions.find((o) => o.value === type);
      if (proto) return proto.label;
      return type;
    },
    statusClass(status) {
      return {
        success: 'label label-success',
        failed: 'label label-danger',
        dead: 'label label-danger',
        in_progress: 'label label-info',
        pending: 'label label-default',
      }[status] || 'label label-default';
    },
    onTypeChange() {
      // Keep type + name; clear the rest so creds never carry across type switches.
      const keep = { type: this.form.type, name: this.form.name };
      this.form = Object.assign(this.blankForm(), keep);
    },
    onProtocolChange() {
      // Keep type + protocol + name + the tailscale key; clear protocol-specific
      // creds so they never carry across an SFTP↔SMB switch.
      const keep = {
        type: this.form.type,
        protocol: this.form.protocol,
        name: this.form.name,
        tailscale_auth_key: this.form.tailscale_auth_key,
      };
      this.form = Object.assign(this.blankForm(), keep);
    },
    connectOauth() {
      // host-pm owns the OAuth flow; this stub captures the returned code.
      const code = window.prompt('Paste the authorization code from ' + this.labelFor(this.form.type));
      if (code) this.form.oauth_code = code;
    },
    payload() {
      const p = Object.assign({}, this.form);
      // On-prem via Tailscale saves under its underlying rclone protocol (sftp/smb);
      // the tailscale_auth_key rides along so host-pm's service joins the tenant tailnet.
      if (p.type === 'tailscale') {
        p.type = p.protocol || '';
      }
      return p;
    },
    testConnection() {
      this.testing = true;
      service.post('drive/backup/targets/test', this.payload(), this.headers())
        .then(() => this.notify('Success', 'Connection OK', 'success'))
        .catch((err) => this.notify('Error', this.errMsg(err, 'Connection failed'), 'error'))
        .then(() => { this.testing = false; });
    },
    saveTarget() {
      service.post('drive/backup/targets', this.payload(), this.headers())
        .then(() => {
          this.notify('Success', 'Target saved', 'success');
          this.form = this.blankForm();
          this.loadTargets();
        })
        .catch((err) => this.notify('Error', this.errMsg(err, 'Could not save target'), 'error'));
    },
    syncNow(targetId) {
      service.post('drive/backup/sync', { target_id: targetId }, this.headers())
        .then(() => { this.notify('Success', 'Sync started', 'success'); setTimeout(() => this.loadHistory(), 1500); })
        .catch((err) => this.notify('Error', this.errMsg(err, 'Could not start sync'), 'error'));
    },
    loadTargets() {
      service.get('drive/backup/targets', this.headers())
        .then((res) => { this.targets = (res.data && res.data.result) || []; })
        .catch((err) => console.log(err));
    },
    loadHistory() {
      service.get('drive/backup/history', this.headers())
        .then((res) => { this.history = (res.data && res.data.result) || []; })
        .catch((err) => console.log(err));
    },
    errMsg(err, fallback) {
      return (err && err.response && err.response.data && err.response.data.status_message) || fallback;
    },
    notify(title, message, type) {
      this.$notify({ title, message, type });
    },
  },
  created() {
    this.loadTargets();
    this.loadHistory();
  },
};
</script>

<style scoped>
.o-truncate { max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.o-plain-list { list-style: none; padding-left: 0; }
.o-src-line { margin: 4px 0 12px; color: #31708f; }
.o-src-line .fa { margin-right: 6px; }
.o-protocol-hint { margin: 6px 0 0; font-size: 12px; color: #888; line-height: 1.4; }
.o-target-instructions {
  border: 1px solid #d6e9c6;
  border-left: 4px solid #3c763d;
  background: #f4faef;
  color: #3c4a3b;
  padding: 10px 12px;
  border-radius: 3px;
  line-height: 1.5;
}
.o-target-instructions ol { margin: 8px 0 4px 18px; padding: 0; }
.o-target-instructions li { margin-bottom: 4px; }
.o-target-instructions code { font-size: 12px; }
.o-dep-note { margin: 8px 0 0; font-size: 12px; color: #8a6d3b; }
.o-coming-soon { margin: 0 0 8px; font-size: 12px; font-style: italic; color: #888; }
.o-sa-key { font-family: monospace; font-size: 12px; white-space: pre; }
.o-ts-callout {
  border: 1px solid #bce8f1;
  border-left: 4px solid #31708f;
  background: #d9edf7;
  color: #245269;
  padding: 10px 12px;
  border-radius: 3px;
  line-height: 1.5;
}
.o-ts-callout .fa { margin-right: 6px; }
</style>

<!-- Non-scoped: the el-select dropdown popper is appended to <body>, so a scoped
     rule can't reach it. Raise the option-list height so all target groups/options
     (~10 rows) are visible without scrolling. -->
<style>
.o-target-popper .el-select-dropdown__wrap { max-height: 400px; }
</style>
